import React, { FC, useRef, useEffect } from 'react'
import { useId } from '@reach/auto-id'
import { useRefWithUpdate } from 'hooks/useRefWithUpdate'
import { transformData } from 'components/Charts/common'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import { XYChart } from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import { Series } from '../Charts/Series'

am4core.useTheme(am4themes_animated)

type ColumnChartProps = {
  data: Series
  selected?: string[]
  onSelect?: (index: string[]) => void
  showLongLabels?: boolean
  truncate?: boolean

}

export const ColumnChart: FC<ColumnChartProps> = ({
  data,
  selected = [],
  onSelect,
  showLongLabels = false,
  truncate = true,
}) => {
  const chartId = useId()
  const chartRef = useRef<XYChart>()
  const transformedData = transformData(data)
  const filters = useRefWithUpdate<string[]>(selected)

  const handleSelection = (value: string) => {
    if (!onSelect) return
    const isSelected = filters.current.includes(value)

    const newSelected = isSelected
      ? filters.current.filter((filter) => value !== filter)
      : [...filters.current, value]

    const allSelected = newSelected.length === data.keys.length
    allSelected ? onSelect([]) : onSelect(newSelected)
  }

  useEffect(() => {
    const chart = am4core.create(chartId, am4charts.XYChart)

    chart.data = transformedData
    chart.zoomOutButton.disabled = false;
    chart.colors.list = data.colors[0].map((c) => am4core.color(c))

    chart.scrollbarX = new am4core.Scrollbar();
    chart.scrollbarX.background.fill = am4core.color("#535584");
    chart.scrollbarX.visible = true;
    chart.scrollbarX.background.fill = am4core.color("#535584");
    chart.scrollbarX.thumb.background.fill = am4core.color("#535584");
    chart.scrollbarX.startGrip.background.fill = am4core.color("#535584");
    chart.scrollbarX.endGrip.background.fill = am4core.color("#535584");
    chart.scrollbarX.stroke = am4core.color("#535584");


    //showLongLabels = true;

    chart.scrollbarX.width = new am4core.Percent(100);
    chart.scrollbarX.minHeight = 5;
    chart.scrollbarX.startGrip.height = 12;
    chart.scrollbarX.endGrip.height = 12;
    chart.scrollbarX.startGrip.width = 12;
    chart.scrollbarX.endGrip.width = 12;

    chart.scrollbarX.isShowing = true;


    createAxes(chart, truncate, showLongLabels)

    data.values.forEach((value, index) => {
      createSeries(chart, 'values', index, handleSelection, filters.current)
    })

    chartRef.current = chart
    //console.log("hola");
    // Remove ugly amCharts icon xD (
    chart.events.on('validated', () => {

      chart.logo.visible = false;

      //document.querySelector(`g[opacity^="0.4"`)?.remove()
    })

    return () => {
      chartRef.current?.dispose()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, `lenght-${data.labels.length}`])

  // Update selection
  useEffect(() => {
    if (!chartRef.current) return

    chartRef.current.series.each((serie) => {
      ; (serie as am4charts.ColumnSeries).columns.values.forEach((column) => {
        const opacity =
          column.dataItem && selected.includes(column.dataItem.categories.categoryX) ? 0.3 : 1
        column.fillOpacity = opacity
      })
    })
  }, [selected, chartRef])

  // -- Update data
  useEffect(() => {
    if (!chartRef.current) return

    transformedData.forEach((_: any, index: number) => {
      const item = chartRef.current!.data[index]
      Object.keys(item).forEach((key) => {
        item[key] = transformedData[index][key]
      })
    })




    chartRef.current.invalidateRawData()
  }, [transformedData])

  return <div id={chartId} style={{ width: '100%', height: '100%' }}></div>
}

const createAxes = (chart: XYChart, truncate: boolean, showLongLabels: boolean) => {
  const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
  categoryAxis.dataFields.category = 'label'

  // Axis and grid appearance
  categoryAxis.renderer.cellStartLocation = 0.1
  categoryAxis.renderer.cellEndLocation = 0.9
  categoryAxis.renderer.grid.template.opacity = 0
  categoryAxis.renderer.ticks.template.opacity = 1
  categoryAxis.renderer.ticks.template.strokeWidth = 1


  categoryAxis.renderer.labels.template.horizontalCenter = "right";
  categoryAxis.renderer.labels.template.verticalCenter = "middle";
  categoryAxis.renderer.labels.template.rotation = 270;
  //categoryAxis.renderer.labels.template.truncate = true;
  categoryAxis.renderer.labels.template.maxWidth = 200;


  // Layout
  categoryAxis.renderer.marginBottom = -20
  categoryAxis.renderer.fontSize = 10

  // Labels appearance
  categoryAxis.renderer.minGridDistance = 50

  //if (showLongLabels) {
  categoryAxis.renderer.labels.template.wrap = false
  //} else {
  // categoryAxis.renderer.labels.template.truncate = truncate
  // categoryAxis.renderer.labels.template.tooltipText = '{category}'
  //}

  // Labels size
  categoryAxis.events.on('sizechanged', function (ev) {
    const axis = ev.target
    const cellWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex)
    axis.renderer.labels.template.maxWidth = cellWidth
  })

  const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
  valueAxis.min = 0
  valueAxis.renderer.line.stroke = am4core.color('#cccccc')
  valueAxis.renderer.line.strokeOpacity = 1
  valueAxis.renderer.fontSize = 12
  valueAxis.renderer.marginLeft = -20
}

const createSeries = (
  chart: XYChart,
  field: string,
  index: number,
  handleSelection: (a: string) => void,
  filters: string[],
) => {
  const series = chart.series.push(new am4charts.ColumnSeries())
  // Binding data to the series
  series.dataFields.valueY = `${field}${index}`
  series.dataFields.categoryX = 'label'
  series.propertyFields.fill = `colors${index}`

  // Animations
  series.interpolationDuration = 500
  series.sequencedInterpolation = true

  series.columns.template.height = am4core.percent(100)
  series.columns.template.strokeWidth = 0

  // Tooltips
  series.columns.template.tooltipText = '{categoryX}: [bold]{valueY}[/]'
  series.columns.template.tooltipY = 0

  if (series.tooltip) {
    series.tooltip.getFillFromObject = false
    series.tooltip.pointerOrientation = 'vertical'
    series.tooltip.dy = -5
    series.tooltip.background.fill = am4core.color('#FFF')
    series.tooltip.background.fillOpacity = 1
    series.tooltip.label.fill = am4core.color('#777')
  }

  // Events
  series.columns.template.events.on('hit', (event) => {
    event.target.dataItem && handleSelection(event.target.dataItem.categories.categoryX)
  })

  series.events.on('ready', (event) => {
    event.target.columns.each((column) => {
      column.fillOpacity =
        column.dataItem && filters.includes(column.dataItem.categories.categoryX) ? 0.3 : 1
    })
  })
}
